import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { useRequestData, useLabels } from 'wsm-common-data';
import { trackEvent } from 'ddc-track-event';
import { addCompareVehicle, removeCompareVehicle } from '../actions/compare';
import { getCompareLink } from '../utilities/compare';
import { getVehicleObj } from '../utilities/vehicleTracking';

const CompareCheckbox = ({ uuid }) => {
	const vehicleObj = getVehicleObj(uuid);

	const dispatch = useDispatch();
	const { compareVehicles } = useSelector((state) => state.compareVehicles);
	const { widgetName, windowId } = useRequestData();
	const labels = useLabels();
	const containsVehicle = useCallback(
		() => compareVehicles.length && compareVehicles.includes(uuid),
		[compareVehicles, uuid]
	);
	const [selected, setSelected] = useState(containsVehicle);
	useEffect(() => {
		// sets the 'DDC.inventory-compare.selected' cookie which the compare page requires
		document.cookie = `DDC.inventory-compare.selected=${compareVehicles.toString()}`;
		// update selected state if compareVehicles are updated from ComparePortal component
		setSelected(containsVehicle);
	}, [compareVehicles, containsVehicle]);

	const compareLink = getCompareLink(compareVehicles);
	const handleChange = (e) => {
		if (window.DDC && window.DDC.pushToGtmDataLayer) {
			const eventData = {
				isSRP: true,
				checked: !selected,
				element: e.target,
				type: vehicleObj.condition,
				...vehicleObj
			};
			delete eventData.condition;
			window.DDC.pushToGtmDataLayer({
				event: 'ddc.link.vehicleCompareCheck',
				eventData
			});
		}
		if (selected) {
			setSelected(false);
			dispatch(removeCompareVehicle(uuid));
			trackEvent(widgetName, windowId, {
				element: 'Deselect comparison selection link',
				result: 'Vehicle removed from comparison'
			});
		} else {
			setSelected(true);
			dispatch(addCompareVehicle(uuid));
			trackEvent(widgetName, windowId, {
				element: 'Compare selection',
				result: 'Vehicle selected for comparison'
			});
		}
	};

	const compareContent = () => {
		if (compareVehicles.length >= 2 && selected) {
			const compareXVehicles = labels
				.get('COMPARE_X_VEHICLES')
				.replace('{count}', compareVehicles.length);
			return (
				<a
					className="ddc-font-size-small"
					href={compareLink}
					onClick={() => {
						trackEvent(widgetName, windowId, {
							element: 'Compare link',
							result: 'Navigated to comparison page'
						});
					}}
				>
					{compareXVehicles}
				</a>
			);
		} else if (compareVehicles.length === 1 && selected) {
			return labels.get('SELECT_MORE_TO_COMPARE');
		} else {
			return null;
		}
	};

	return (
		<React.Fragment>
			<label
				className="pointer ddc-font-size-small text-link text-link-muted mb-0 mr-4"
				htmlFor={`${uuid}-compare`}
			>
				<input
					type="checkbox"
					checked={selected}
					onChange={(e) => handleChange(e)}
					id={`${uuid}-compare`}
				/>
				<span className="pl-2">{labels.get('COMPARE')}</span>
			</label>
			<div className="order-1 w-100 pt-2 ddc-font-size-small">
				{compareContent()}
			</div>
		</React.Fragment>
	);
};

CompareCheckbox.propTypes = {
	uuid: PropTypes.string.isRequired
};

export default CompareCheckbox;
